import React, { useState, useEffect } from 'react';
import { supplierService } from '../../services/supplierService';
import { migoService } from '../../services/migoService';
import Toast from '../Common/Toast';

const CreateSupplierModal = ({ isOpen, onClose, onSuccess, forceProviderOnly = true, clientOnly = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    document_type: 'ruc',
    document_number: '',
    is_active: true,
    is_provider: !clientOnly,
    is_client: clientOnly,
    is_internal: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        document_type: 'ruc',
        document_number: '',
        is_active: true,
        is_provider: clientOnly ? false : true,
        is_client: clientOnly,
        is_internal: false,
      });
      setErrors({});
    }
  }, [isOpen, clientOnly]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      
      // Si cambia el tipo de documento, limpiar el número y generar código si es "other"
      if (name === 'document_type') {
        newData.document_number = '';
        if (value === 'other') {
          const timestamp = Date.now().toString().slice(-6);
          const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
          newData.document_number = `OTRO-${timestamp}-${random}`;
        }
      }
      
      return newData;
    });
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleDocumentBlur = async () => {
    const docNumber = formData.document_number?.trim();
    const docType = formData.document_type;
    
    if (docType !== 'other' && docNumber && 
        ((docType === 'ruc' && docNumber.length === 11) || 
         (docType === 'dni' && docNumber.length === 8))) {
      setLoadingDocument(true);
      
      try {
        let response;
        if (docType === 'ruc') {
          response = await migoService.consultRUC(docNumber);
        } else if (docType === 'dni') {
          response = await migoService.consultDNI(docNumber);
        }
        
        if (response && response.success && response.data) {
          const apiData = response.data;
          const newName = apiData.name ? String(apiData.name).trim() : '';
          
          if (newName) {
            setFormData(prev => ({
              ...prev,
              name: newName,
            }));
          }
        }
      } catch (error) {
        console.error('Error al consultar documento:', error);
      } finally {
        setLoadingDocument(false);
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    if (formData.document_number) {
      if (formData.document_type === 'ruc' && formData.document_number.length !== 11) {
        newErrors.document_number = 'El RUC debe tener 11 dígitos';
      } else if (formData.document_type === 'dni' && formData.document_number.length !== 8) {
        newErrors.document_number = 'El DNI debe tener 8 dígitos';
      }
    }

    if (!forceProviderOnly && !clientOnly && !formData.is_provider && !formData.is_client && !formData.is_internal) {
      newErrors.entity_types = 'Seleccione al menos un tipo de entidad';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    
    try {
      const supplierData = {
        name: formData.name,
        document_type: formData.document_type,
        document_number: formData.document_number || null,
        is_active: formData.is_active,
        is_provider: forceProviderOnly ? true : (clientOnly ? false : formData.is_provider === true),
        is_client: forceProviderOnly ? false : (clientOnly ? true : formData.is_client === true),
        is_internal: forceProviderOnly || clientOnly ? false : formData.is_internal === true,
      };
      
      const response = await supplierService.createSupplier(supplierData);
      
      if (response.success) {
        setToast({
          isOpen: true,
          message: forceProviderOnly
            ? 'Proveedor creado exitosamente'
            : clientOnly
              ? 'Cliente creado exitosamente'
              : 'Entidad creada exitosamente',
          type: 'success'
        });
        
        setTimeout(() => {
          if (onSuccess) {
            onSuccess(response.data);
          }
          onClose();
        }, 1000);
      } else {
        throw new Error(
          response.message || (forceProviderOnly ? 'Error al crear el proveedor' : 'Error al crear la entidad')
        );
      }
    } catch (error) {
      console.error('Error al crear proveedor:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        (forceProviderOnly ? 'Error al crear el proveedor' : 'Error al crear la entidad');
      const errorErrors = error.response?.data?.errors || {};
      
      setErrors({
        submit: errorMessage,
        ...errorErrors,
      });
      
      setToast({
        isOpen: true,
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <div>
            {/* Header */}
            <div className="bg-teal-600 px-6 py-4">
              <h3 className="text-lg font-medium text-white">
                {forceProviderOnly ? 'Nuevo Proveedor' : clientOnly ? 'Nuevo Cliente' : 'Nueva Entidad'}
              </h3>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4">
              {errors.submit && (
                <div className="bg-red-50 text-red-700 p-3 rounded text-sm border border-red-200">
                  {errors.submit}
                </div>
              )}

              {/* Tipo de Documento */}
              <div>
                <label htmlFor="document_type" className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Documento
                </label>
                <select
                  id="document_type"
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="ruc">RUC</option>
                  <option value="dni">DNI</option>
                  <option value="other">Otro</option>
                </select>
              </div>

              {/* Número de Documento */}
              <div>
                <label htmlFor="document_number" className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.document_type === 'ruc' ? 'RUC' : formData.document_type === 'dni' ? 'DNI' : 'Código'}
                  {loadingDocument && (
                    <span className="ml-2 text-xs text-teal-600">Consultando...</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="document_number"
                    name="document_number"
                    value={formData.document_number}
                    onChange={handleChange}
                    onBlur={handleDocumentBlur}
                    maxLength={formData.document_type === 'ruc' ? 11 : formData.document_type === 'dni' ? 8 : undefined}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      errors.document_number ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                    }`}
                    placeholder={
                      formData.document_type === 'ruc' 
                        ? '20123456789' 
                        : formData.document_type === 'dni' 
                        ? '43451826' 
                        : 'Código generado automáticamente'
                    }
                    disabled={loadingDocument || formData.document_type === 'other'}
                    readOnly={formData.document_type === 'other'}
                  />
                  {loadingDocument && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                    </div>
                  )}
                </div>
                {errors.document_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.document_number}</p>
                )}
              </div>

              {/* Nombre */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre o Razón Social <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.name ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                  }`}
                  placeholder="Ej: Distribuidora ABC S.A.C."
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {!forceProviderOnly && !clientOnly && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Entidades</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_provider"
                        checked={formData.is_provider}
                        onChange={handleChange}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Proveedor</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_client"
                        checked={formData.is_client}
                        onChange={handleChange}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Cliente</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_internal"
                        checked={formData.is_internal}
                        onChange={handleChange}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Interno</span>
                    </label>
                  </div>
                  {errors.entity_types && (
                    <p className="mt-1 text-sm text-red-600">{errors.entity_types}</p>
                  )}
                </div>
              )}

              {clientOnly && (
                <div className="rounded-md bg-teal-50 border border-teal-100 px-3 py-2 text-sm text-teal-800">
                  Desde Ventas solo se crean entidades de tipo <strong>Cliente</strong>.
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default CreateSupplierModal;

